import api from '../index'

export interface TradeOrderItemSnapshot {
  id: number
  order_id: number
  goods_id: number
  goods_no_snapshot: string
  goods_title_snapshot: string
  goods_image_snapshot: string
  goods_content_snapshot: string | null
  price_snapshot: number | string
  quantity: number
  subtotal_amount: number | string
  created_at: string | null
  updated_at: string | null
  version: number
}

export interface TradePaymentOrderItem {
  id: number
  payment_order_no: string
  biz_order_no: string
  order_id: number
  buyer_user_id: number
  seller_user_id: number
  seller_merchant_id: number
  scene_type: string
  pay_id: number
  pay_type: string
  pay_channel: string
  client_type: number
  amount: string
  status: string
  status_text?: string
  alipay_trade_no: string | null
  third_payment_payload?: Record<string, any> | null
  notify_payload?: Record<string, any> | null
  fail_reason: string | null
  paid_at: string | null
  closed_at: string | null
  created_at: string | null
  updated_at: string | null
  can_query_result?: number
}

export interface TradeRefundOrderItem {
  id: number
  refund_order_no: string
  order_id: number
  payment_order_id: number
  order_no: string
  payment_order_no: string
  buyer_user_id: number
  seller_user_id: number
  seller_merchant_id: number
  refund_type: string
  refund_type_text?: string
  refund_status: string
  refund_status_text?: string
  apply_reason: string
  apply_reason_image: string
  refund_reason: string
  refund_amount: string
  third_refund_no: string | null
  review_remark: string | null
  reviewed_by: number
  applied_at: string | null
  approved_at: string | null
  rejected_at: string | null
  refunded_at: string | null
  notify_payload?: Record<string, any> | null
  created_at: string | null
  updated_at: string | null
  can_execute?: number
  can_query_result?: number
  collection_order_id?: number
  collection_order_no?: string
  product_key?: string
  gateway_account_code?: string
  refund_goods_amount?: string
  refund_postage_amount?: string
  channel_refund_id?: string | null
  channel_refund_status?: string
  channel_refund_status_text?: string
  fund_flow_status?: string
  fund_flow_status_text?: string
  sla_policy_version?: string
  seller_review_due_at?: string | null
  finance_execute_due_at?: string | null
  channel_submitted_at?: string | null
  channel_result_due_at?: string | null
  sla_state?: 'normal' | 'due_soon' | 'overdue' | 'missing' | 'not_applicable'
  sla_stage?: string
  sla_stage_text?: string
  sla_deadline_at?: string | null
  sla_remaining_seconds?: number | null
  waiting_party?: string
  waiting_party_text?: string
}

export interface C2cCollectionEventItem {
  id: number
  collection_order_id: number
  event_id: string
  event_type: string
  status: string
  status_text?: string
  fail_reason: string | null
  created_at: string | null
  processed_at: string | null
}

export interface C2cSettlementOrderItem {
  id: number
  settlement_order_no: string
  collection_order_id: number
  trade_order_id: number
  settlement_base_amount: string
  platform_service_fee: string
  seller_settlement_amount: string
  status: string
  status_text?: string
  channel_settlement_no?: string | null
  attempt_count: number
  next_retry_at?: string | null
  last_error_code?: string | null
  last_error_message?: string | null
  settled_at?: string | null
  created_at?: string | null
}

export interface C2cCollectionOrderItem {
  id: number
  collection_order_no: string
  trade_order_id: number
  trade_order_no: string
  buyer_user_id: number
  seller_user_id: number
  seller_merchant_id: number
  seller_receiving_account_id: number
  product_key: string
  provider_key: string
  gateway_account_code: string
  sub_mchid_snapshot: string
  goods_paid_amount: string
  postage_amount: string
  platform_subsidy_amount: string
  pay_amount: string
  refunded_goods_amount: string
  refunded_amount: string
  status: string
  status_text: string
  fund_status: string
  fund_status_text: string
  channel_order_no: string | null
  channel_transaction_id: string | null
  fail_reason: string
  paid_at: string | null
  closed_at: string | null
  created_at: string | null
  updated_at: string | null
  version: number
  amount?: string
  method_key?: string
}

export interface C2cCollectionOrderDetail extends C2cCollectionOrderItem {
  trade_order?: TradeOrderItem | null
  events: C2cCollectionEventItem[]
  fee_snapshot?: Record<string, any> | null
  fee_adjustments: Array<Record<string, any>>
  settlement_order?: C2cSettlementOrderItem | null
  refund_orders: TradeRefundOrderItem[]
  profit_sharing_returns: Array<Record<string, any>>
  recovery_actions?: Array<Record<string, any>>
}

export interface C2cFundStats {
  total: number
  created: number
  paying: number
  paid: number
  closed: number
  pay_failed: number
  fund_status: Record<string, number>
  paid_amount: string
  refunded_amount: string
}

export interface TradeOrderActionLogItem {
  id: number
  order_id: number
  order_no: string
  action: string
  operator_type: string
  operator_user_id: number
  before_status: string
  after_status: string
  payload_json?: Record<string, any> | null
  created_at: string | null
}

export interface TradeDeliveryLogItem {
  id: number
  order_id: number
  order_no: string
  delivery_company_id: number
  delivery_name: string
  delivery_code: string
  delivery_type: string
  delivery_no: string
  operator_user_id: number
  remark: string | null
  payload_json?: Record<string, any> | null
  created_at: string | null
}

export interface TradeOrderItem {
  id: number
  order_no: string
  buyer_user_id: number
  seller_user_id: number
  seller_merchant_id: number
  buyer_nickname_snapshot?: string
  buyer_avatar_snapshot?: string
  seller_nickname_snapshot?: string
  seller_avatar_snapshot?: string
  goods_id: number
  goods_no_snapshot?: string
  goods_title_snapshot: string
  goods_image_snapshot: string
  goods_content_snapshot?: string | null
  goods_price_snapshot: number | string
  address_snapshot_json?: Record<string, any> | null
  remark: string | null
  goods_num: number
  total_price: number | string
  total_postage: number | string
  discount_amount: number | string
  pay_price: string
  pay_id: number
  pay_type: string
  pay_channel: string
  payment_status: string
  payment_status_text?: string
  order_status: string
  order_status_text?: string
  refund_status: string
  refund_status_text?: string
  active_dispute_case_id?: number | null
  dispute_status?: string
  dispute_stage?: string
  dispute_stage_text?: string
  dispute_started_at?: string | null
  dispute_resolved_at?: string | null
  delivery_name: string
  delivery_code: string
  delivery_type: string
  delivery_id: string
  paid_at: string | null
  shipped_at: string | null
  received_at: string | null
  cancelled_at: string | null
  completed_at: string | null
  expire_at: string | null
  created_at: string | null
  updated_at: string | null
  latest_collection_order_id?: number
  latest_collection_order_no?: string
  /** 旧页面兼容字段；C2C 订单管理不得再用它作为支付事实。 */
  latest_payment_order_id?: number
  latest_payment_order_no?: string
  latest_refund_order_id?: number
  latest_refund_order_no?: string
  latest_settlement_order_id?: number
  latest_settlement_order_no?: string
  latest_settlement_status?: string
  latest_dispute_case_id?: number
  latest_dispute_case_no?: string
  latest_dispute_status?: string
  payment_relation_state?: 'available' | 'missing' | 'not_required'
  payment_relation_text?: string
  payment_relation_reason?: string
  payment_relation_reason_text?: string
  refund_relation_state?: 'available' | 'missing' | 'not_required'
  refund_relation_text?: string
  refund_relation_reason?: string
  refund_relation_reason_text?: string
  dispute_relation_state?: 'available' | 'missing' | 'not_required'
  dispute_relation_text?: string
  dispute_relation_reason?: string
  dispute_relation_reason_text?: string
  channel_event_count?: number
  latest_channel_event_type?: string
  latest_channel_event_status?: string
  latest_channel_event_at?: string | null
  channel_evidence_state?: 'available' | 'missing' | 'not_required'
  channel_evidence_text?: string
  channel_evidence_reason?: string
  channel_evidence_reason_text?: string
  settlement_relation_state?: 'available' | 'missing' | 'not_required'
  settlement_relation_text?: string
  settlement_relation_reason?: string
  settlement_relation_reason_text?: string
  relation_has_exception?: number
  relation_exception_count?: number
  relation_exception_summary?: string
  tracking_stopped?: number
  tracking_stopped_at?: string
  tracking_stop_reason?: string
  tracking_stop_reason_text?: string
  delivery_status?: string
  delivery_status_text?: string
  delivery_latest_time?: string
  delivery_latest_context?: string
  delivery_refreshable?: number
  exception_type?: string
  exception_type_text?: string
  exception_level?: string
  exception_summary?: string
  waiting_party?: 'buyer' | 'seller' | 'platform' | 'finance' | 'none'
  waiting_party_text?: string
  fulfillment_deadline_at?: string | null
  remaining_seconds?: number | null
  remaining_text?: string
  sla_state?: 'normal' | 'due_soon' | 'overdue' | 'paused' | 'missing' | 'not_applicable'
  sla_stage?: string
  sla_stage_text?: string
  sla_reason_code?: string
  sla_reason_text?: string
  sla_policy_version?: string
  sla_is_exception?: number
  next_action?: string
}

export interface TradeOrderDetail extends TradeOrderItem {
  items: TradeOrderItemSnapshot[]
  collection_orders: C2cCollectionOrderItem[]
  payment_orders: TradePaymentOrderItem[]
  refund_orders: TradeRefundOrderItem[]
  collection_events: C2cCollectionEventItem[]
  settlement_orders: C2cSettlementOrderItem[]
  delivery_logs: TradeDeliveryLogItem[]
  action_logs: TradeOrderActionLogItem[]
}

export interface TradeOrderDeliveryRefreshDetail {
  order_id: string
  delivery: Record<string, any> | null
}

export interface TradePaymentOrderDetail extends TradePaymentOrderItem {
  trade_order?: TradeOrderItem | null
  refund_orders: TradeRefundOrderItem[]
}

export interface TradeRefundOrderDetail extends TradeRefundOrderItem {
  order?: TradeOrderItem | null
  payment_order?: TradePaymentOrderItem | null
  collection_order?: C2cCollectionOrderItem | null
  settlement_order?: Record<string, any> | null
  profit_sharing_return_order?: Record<string, any> | null
}

export interface PagePayload<T> {
  list: T[]
  total: number
  page: number
  per_page: number
}

export interface TradeOrderStats {
  filtered_total: number
  all: number
  pending_payment: number
  paid_pending_shipment: number
  shipped: number
  completed: number
  refunding: number
  refunded: number
  closed: number
  relation_exceptions: number
  payment_relation_exceptions: number
  refund_relation_exceptions: number
  dispute_relation_exceptions: number
  channel_evidence_exceptions: number
  settlement_relation_exceptions: number
  sla_missing: number
  sla_due_soon: number
  sla_overdue: number
}

interface ApiEnvelope<T> { code: number, message: string, data: T }

const orderUrl = '/admin/trade/order'

function getPage<T>(url: string, params: Record<string, unknown>) {
  return api.get<ApiEnvelope<PagePayload<T>>>(url, { params }) as unknown as Promise<ApiEnvelope<PagePayload<T>>>
}

function getDetail<T>(url: string, id: number) {
  return api.get<ApiEnvelope<T>>(`${url}/${id}`) as unknown as Promise<ApiEnvelope<T>>
}

async function getTradeOrderDetail(id: number): Promise<ApiEnvelope<TradeOrderDetail>> {
  const response = await getDetail<TradeOrderDetail>(orderUrl, id)
  return {
    ...response,
    data: {
      ...response.data,
      items: response.data.items ?? [],
      collection_orders: response.data.collection_orders ?? [],
      payment_orders: response.data.payment_orders ?? [],
      refund_orders: response.data.refund_orders ?? [],
      collection_events: response.data.collection_events ?? [],
      settlement_orders: response.data.settlement_orders ?? [],
      delivery_logs: response.data.delivery_logs ?? [],
      action_logs: response.data.action_logs ?? [],
    },
  }
}

const apiTrade = {
  order: {
    page: (params: Record<string, unknown>) => getPage<TradeOrderItem>(orderUrl, params),
    stats: (params: Record<string, unknown>) => api.get<ApiEnvelope<TradeOrderStats>>(`${orderUrl}/stats`, { params }) as unknown as Promise<ApiEnvelope<TradeOrderStats>>,
    detail: (id: number) => getTradeOrderDetail(id),
    refreshDelivery: (id: number) => api.post<ApiEnvelope<TradeOrderDeliveryRefreshDetail>>(`${orderUrl}/${id}/refresh-delivery`) as unknown as Promise<ApiEnvelope<TradeOrderDeliveryRefreshDetail>>,
  },
}

export default apiTrade
